import { get, isPlainObject, pick } from 'lodash'

import { hashString } from './hash'
import { TEXT_PROPS } from './protocol'

const CUSTOM_KEYS = ['blockId', 'blockVersion', 'state', 'options']
const VALUE_KEYS = ['desktop', 'default', 'mobile']

const stripHtml = (value = '') => String(value)
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const plainValue = value => {
  if (value === null || value === undefined) {
    return undefined
  }

  if (typeof value !== 'object') {
    return String(value)
  }

  const key = VALUE_KEYS.find(item => value[item] !== undefined)
  if (key) {
    return plainValue(value[key])
  }

  return Object.values(value).find(item => typeof item === 'string')
}

function nodeText(block, properties) {
  const prop = TEXT_PROPS[block]
  if (!prop) {
    return undefined
  }

  const value = plainValue(get(properties, prop))
  return value === undefined ? undefined : stripHtml(value)
}

const hashNodeDoc = doc => hashString(JSON.stringify(doc))

function buildNodeDoc(id, serialized) {
  const { type, props, custom = {}, nodes = [], linkedNodes = {}, parent = null, hidden = false, isCanvas = false } = serialized
  const block = typeof type === 'string' ? type : type?.resolvedName
  const properties = isPlainObject(props?.properties) ? props.properties : {}
  const { attributes, ...rest } = properties
  const { className = '', ...attrs } = isPlainObject(attributes) ? attributes : {}

  const doc = {
    id,
    block,
    name: custom.name || block,
    parent,
    children: [...nodes],
    linked: { ...linkedNodes },
    hidden: !!hidden,
    canvas: !!isCanvas,
    classes: className,
  }

  const text = nodeText(block, properties)
  if (text !== undefined) {
    doc.text = text
  }

  doc.props = Object.keys(attrs).length ? { ...rest, attributes: attrs } : rest

  const extra = pick(custom, CUSTOM_KEYS)
  if (Object.keys(extra).length) {
    doc.custom = extra
  }

  if (id === 'ROOT' && custom.metadata) {
    doc.metadata = custom.metadata
  }

  doc.hash = hashNodeDoc(doc)
  return doc
}

export function buildDocument(serializedNodes, { root = 'ROOT' } = {}) {
  const nodes = []

  const visit = (id, index) => {
    const serialized = serializedNodes[id]
    if (!serialized) {
      return
    }

    const doc = buildNodeDoc(id, serialized)
    doc.index = index
    nodes.push(doc)

    Object.keys(doc.linked).sort().forEach(name => visit(doc.linked[name], 0))
    doc.children.forEach((childId, childIndex) => visit(childId, childIndex))
  }

  visit(root, 0)

  return {
    root,
    count: nodes.length,
    hash: hashString(nodes.map(node => `${node.id}:${node.hash}`).join('|')),
    nodes,
  }
}

export const indexDocument = document => new Map(document.nodes.map(node => [node.id, node]))
